/**
 * Urgentarea ceruta curierilor (28.10.2026): adresele fiecarui curier, exemplul, pornirea si
 * istoricul emailurilor trimise. Logica e in API (`fan/urgentare.ts`); aici doar setarile.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { Pastila } from '../../../components/ui/Pastila'
import { mesajServer, numeCurier } from './tipuri'

type Curier = 'FAN' | 'SAMEDAY'

interface SetareUrgentare {
  courier: Curier
  catre: string[]
  cc: string[]
  raspunsLa: string[]
  pornit: boolean
  pornitLa: string | null
  pornitDe: string | null
  /** Ultima rulare oprita de pragul de 50 de AWB noi. */
  blocatLa: string | null
  blocatNumar: number | null
  /** Cate s-ar cere acum (pentru „Trimite azi, deși sunt N"). */
  deCerutAzi: number
  /** Intarziate deja la pornire: nu se cer automat. */
  dinainte: number
}

interface Trimitere {
  id: number
  courier: Curier
  catre: string[]
  cc: string[]
  subiect: string
  numarAwb: number
  status: 'trimis' | 'eroare'
  eroare: string | null
  createdAt: string
}

const NUME: Record<Curier, string> = { FAN: 'FAN Courier', SAMEDAY: 'Sameday' }

/** Adresele scrise cu virgulă, punct și virgulă sau câte una pe rând. */
const adrese = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter(Boolean)

const cand = (s: string) =>
  new Date(s).toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export function SetariUrgentare({ editabil }: { editabil: boolean }) {
  const [vazuta, setVazuta] = useState<number | null>(null)
  const { data, isError } = useQuery<{ curieri: SetareUrgentare[]; trimiteri: Trimitere[] }>({
    queryKey: ['fan', 'urgentare'],
    queryFn: async () => (await api.get('/fan/urgentare')).data,
  })

  if (isError) return <p className="text-sm text-b4l-red-dark">Nu am putut aduce setările.</p>
  if (!data) return <p className="text-sm text-b4l-text-muted">Se încarcă…</p>

  return (
    <div className="space-y-4 text-sm">
      <p className="text-b4l-text-muted">
        În fiecare zi lucrătoare la 9:00, CRM-ul cere fiecărui curier pornit să urgenteze livrarea
        coletelor preluate de cel puțin 4 zile lucrătoare. Fiecare curier primește doar AWB-urile
        lui, iar fiecare colet se cere o singură dată. Curierul vede doar AWB-ul, data preluării și
        zilele lucrătoare de la preluare. Automat se cer doar coletele care intră în întârziere după
        pornire.
      </p>
      {data.curieri.map((s) => (
        <CardCurier
          key={`${s.courier}-${s.catre.join()}-${s.cc.join()}-${s.raspunsLa.join()}`}
          s={s}
          editabil={editabil}
        />
      ))}

      <div>
        <p className="mb-1 font-medium">Istoric trimiteri</p>
        {data.trimiteri.length === 0 ? (
          <p className="text-xs text-b4l-text-muted">Nu s-a trimis încă nimic curierilor.</p>
        ) : (
          <ul className="max-h-56 divide-y divide-b4l-border overflow-y-auto rounded border border-b4l-border">
            {data.trimiteri.map((t) => (
              <li key={t.id} className="flex items-start gap-2 px-2 py-1.5 text-xs">
                <div className="min-w-0 flex-1">
                  <div>
                    <span className="tabular-nums">{cand(t.createdAt)}</span> ·{' '}
                    {numeCurier(t.courier)} · {t.numarAwb} AWB
                  </div>
                  <div className="truncate text-b4l-text-dim">
                    către {t.catre.join(', ')}
                    {t.cc.length > 0 && ` · CC ${t.cc.join(', ')}`}
                  </div>
                  {t.eroare && <div className="text-b4l-red-dark">{t.eroare}</div>}
                </div>
                <Pastila ton={t.status === 'trimis' ? 'verde' : 'rosu'}>
                  {t.status === 'trimis' ? 'Trimis' : 'Netrimis'}
                </Pastila>
                <button className="text-b4l-blue-dark underline" onClick={() => setVazuta(t.id)}>
                  Vezi emailul
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {vazuta != null && <EmailTrimis id={vazuta} onClose={() => setVazuta(null)} />}
    </div>
  )
}

function CardCurier({ s, editabil }: { s: SetareUrgentare; editabil: boolean }) {
  const qc = useQueryClient()
  const [catre, setCatre] = useState(s.catre.join('\n'))
  const [cc, setCc] = useState(s.cc.join('\n'))
  const [raspunsLa, setRaspunsLa] = useState(s.raspunsLa.join('\n'))
  const nume = NUME[s.courier]

  const modificat =
    adrese(catre).join() !== s.catre.join() ||
    adrese(cc).join() !== s.cc.join() ||
    adrese(raspunsLa).join() !== s.raspunsLa.join()

  const reimprospateaza = () => qc.invalidateQueries({ queryKey: ['fan', 'urgentare'] })

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/fan/urgentare/${s.courier}`, {
          catre: adrese(catre),
          cc: adrese(cc),
          raspunsLa: adrese(raspunsLa),
        })
      ).data as { oprit: boolean },
    onSuccess: (r) => {
      toast.success(
        r.oprit
          ? `Adresele au fost salvate. Trimiterea către ${nume} s-a oprit — trimite-ți exemplul și pornește-o din nou.`
          : 'Adresele au fost salvate.',
      )
      void reimprospateaza()
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Salvarea a eșuat.')),
  })

  const exemplu = useMutation({
    mutationFn: async () =>
      (await api.post(`/fan/urgentare/${s.courier}/exemplu`, {})).data as {
        awb: number
        mesaj: string
        pesteLimita: boolean
      },
    onSuccess: (r) =>
      r.awb === 0
        ? toast.info(r.mesaj)
        : r.pesteLimita
          ? toast.warning(r.mesaj)
          : toast.success(r.mesaj),
    onError: (e: unknown) => toast.error(mesajServer(e, 'Exemplul nu a putut fi trimis.')),
  })

  const [confirmare, setConfirmare] = useState(false)
  // Adresele afisate merg cu pornirea: daca le-a schimbat altcineva intre timp, serverul refuza.
  const comuta = useMutation({
    mutationFn: async (porneste: boolean) =>
      (
        await api.post(
          `/fan/urgentare/${s.courier}/${porneste ? 'porneste' : 'opreste'}`,
          porneste ? { catre: s.catre, cc: s.cc } : {},
        )
      ).data,
    onSuccess: (_r, porneste) => {
      toast.success(
        porneste ? `Trimiterea către ${nume} e pornită.` : `Trimiterea către ${nume} e oprită.`,
      )
      void reimprospateaza()
    },
    onError: (e: unknown) => {
      toast.error(mesajServer(e, 'Nu s-a putut schimba.'))
      void reimprospateaza()
    },
  })

  const trimiteAzi = useMutation({
    mutationFn: async () =>
      (await api.post(`/fan/urgentare/${s.courier}/trimite-azi`, { numar: s.deCerutAzi })).data as {
        awb: number
        status: string
      },
    onSuccess: (r) => {
      setConfirmare(false)
      if (r.status === 'neinregistrat') {
        toast.warning(
          `Emailul cu ${r.awb} AWB a plecat la ${nume}, dar coletele nu s-au putut înregistra ca cerute. Verifică.`,
        )
      } else toast.success(`Am cerut la ${nume} urgentarea a ${r.awb} AWB.`)
      void qc.invalidateQueries({ queryKey: ['fan'] })
    },
    onError: (e: unknown) => {
      setConfirmare(false)
      toast.error(mesajServer(e, 'Trimiterea nu a reușit.'))
      void reimprospateaza()
    },
  })

  const stare = s.pornit ? (
    <Pastila ton="verde">Pornit</Pastila>
  ) : s.catre.length === 0 ? (
    <Pastila ton="gri">Neconfigurat</Pastila>
  ) : (
    <Pastila ton="galben">Oprit</Pastila>
  )

  const camp = (eticheta: string, v: string, setV: (x: string) => void, ajutor?: string) => (
    <label className="block">
      <span className="mb-0.5 block text-[11px] text-b4l-text-muted">{eticheta}</span>
      <textarea
        rows={2}
        className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
        value={v}
        disabled={!editabil}
        onChange={(e) => setV(e.target.value)}
      />
      {ajutor && <span className="mt-0.5 block text-xs text-b4l-text-muted">{ajutor}</span>}
    </label>
  )

  return (
    <div className="space-y-2 rounded border border-b4l-border p-3">
      <div className="flex items-center gap-2">
        <span className="font-medium">{nume}</span>
        {stare}
        {s.pornit && s.pornitLa && (
          <span className="text-xs text-b4l-text-dim">
            din {cand(s.pornitLa)}
            {s.pornitDe ? `, de ${s.pornitDe}` : ''}
          </span>
        )}
      </div>
      {s.pornit && s.blocatLa && (
        <div className="space-y-1.5 rounded border border-b4l-red/30 bg-b4l-red-light/30 p-2 text-xs">
          <p className="font-medium text-b4l-red-dark">
            Pe {cand(s.blocatLa)} erau {s.blocatNumar} AWB noi — peste limita de 50, nu s-a trimis
            nimic curierului.
          </p>
          <p>
            Probabil e o eroare în date. Verifică lista din emailul nostru; dacă e corectă,
            trimite-o azi. Altfel, lista se verifică din nou în fiecare zi lucrătoare la 9:00.
          </p>
          {editabil && s.deCerutAzi > 0 && (
            <Button variant="secondary" onClick={() => setConfirmare(true)}>
              Trimite azi, deși sunt {s.deCerutAzi}
            </Button>
          )}
        </div>
      )}
      {s.pornit && s.dinainte > 0 && (
        <p className="text-xs text-b4l-text-muted">
          {s.dinainte === 1
            ? '1 colet era deja întârziat la pornire; nu se cere automat.'
            : `${s.dinainte} colete erau deja întârziate la pornire; nu se cer automat.`}
        </p>
      )}
      {confirmare && (
        <Modal
          open
          onClose={() => setConfirmare(false)}
          title={`Trimiți la ${nume} ${s.deCerutAzi} AWB?`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmare(false)}>
                Renunță
              </Button>
              <Button onClick={() => trimiteAzi.mutate()} loading={trimiteAzi.isPending}>
                Trimite acum
              </Button>
            </>
          }
        >
          <p className="text-sm">
            Pleacă acum un singur email la {s.catre.join(', ')}, cu toate cele {s.deCerutAzi}{' '}
            AWB-uri (și fișierul Excel). Fiecare se notează ca cerut și nu se mai cere niciodată.
            Azi nu mai pleacă alt email la {nume}.
          </p>
        </Modal>
      )}
      {camp(
        'Către (adresele curierului)',
        catre,
        setCatre,
        'Câte una pe rând sau despărțite prin virgulă.',
      )}
      {camp('CC', cc, setCc)}
      {camp(
        'Răspunsurile vin la',
        raspunsLa,
        setRaspunsLa,
        'Când curierul răspunde, mesajul ajunge la aceste adrese.',
      )}
      {editabil && (
        <>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="secondary"
              onClick={() => salveaza.mutate()}
              loading={salveaza.isPending}
              disabled={!modificat}
            >
              Salvează
            </Button>
            <Button variant="ghost" onClick={() => exemplu.mutate()} loading={exemplu.isPending}>
              Trimite-mi exemplul de azi
            </Button>
            <span className="ml-auto" />
            {s.pornit ? (
              <Button
                variant="secondary"
                onClick={() => comuta.mutate(false)}
                loading={comuta.isPending}
              >
                Oprește
              </Button>
            ) : (
              <Button
                onClick={() => comuta.mutate(true)}
                loading={comuta.isPending}
                disabled={modificat || s.catre.length === 0}
              >
                Pornește trimiterea către {nume}
              </Button>
            )}
          </div>
          {s.pornit && (
            <p className="text-xs text-b4l-text-muted">
              Dacă schimbi adresele de la „Către” sau „CC”, trimiterea se oprește și trebuie pornită
              din nou.
            </p>
          )}
          {!s.pornit && modificat && (
            <p className="text-xs text-b4l-text-muted">Salvează adresele înainte de pornire.</p>
          )}
        </>
      )}
    </div>
  )
}

/** Emailul trimis, exact cum a plecat. HTML-ul e al nostru, dar tot izolat în iframe. */
function EmailTrimis({ id, onClose }: { id: number; onClose: () => void }) {
  const { data } = useQuery<Trimitere & { html: string }>({
    queryKey: ['fan', 'urgentare', 'trimitere', id],
    queryFn: async () => (await api.get(`/fan/urgentare/trimiteri/${id}`)).data,
  })
  return (
    <Modal open onClose={onClose} title={data?.subiect ?? 'Email trimis'} size="xl">
      {!data ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
      ) : (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-b4l-text-muted">
            {cand(data.createdAt)} · către {data.catre.join(', ')}
            {data.cc.length > 0 && ` · CC ${data.cc.join(', ')}`}
          </p>
          <iframe
            title="Emailul trimis"
            sandbox=""
            srcDoc={data.html}
            className="h-[60vh] w-full rounded border border-b4l-border bg-white"
          />
        </div>
      )}
    </Modal>
  )
}

/** Eticheta din panoul coletului: cerut curierului (cand, cui) sau de ce nu. */
export function EtichetaUrgentare({
  courier,
  urgentari,
}: {
  courier: string
  urgentari?: {
    fel: 'cerut' | 'exclus'
    motiv: string | null
    createdAt: string
  }[]
}) {
  if (!urgentari?.length) return null
  const cerut = urgentari.find((u) => u.fel === 'cerut')
  if (cerut) {
    const zi = new Date(cerut.createdAt).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
    })
    return (
      <Pastila ton="albastru">
        Cerut la {numeCurier(courier)} · {zi}
      </Pastila>
    )
  }
  const exclus = urgentari.find((u) => u.fel === 'exclus')
  return exclus ? <Pastila ton="gri">Necerut curierului: {exclus.motiv}</Pastila> : null
}
